import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ToastProvider, useToast } from './ui/Toast';
import { Topbar } from './shell/Topbar';
import { Sidebar } from './shell/Sidebar';
import { AreaErrorBoundary } from './shell/AreaErrorBoundary';
import { ImpersonateBanner } from './shell/ImpersonateBanner';
import { Login } from './areas/Login';
import { Companies } from './areas/Companies';
import { Files } from './areas/Files';
import { Dashboard } from './areas/Dashboard';
import { Logs } from './areas/Logs';
import { Providers } from './areas/Providers';
import { Templates } from './areas/Templates';
import { Docs } from './areas/Docs';
import { Account } from './areas/Account';

const VALID_AREAS = ['dashboard', 'files', 'logs', 'companies', 'providers', 'templates', 'docs', 'account'] as const;

function areaFromUrl(): string {
  const a = new URLSearchParams(window.location.search).get('area');
  return a && (VALID_AREAS as readonly string[]).includes(a) ? a : 'dashboard';
}

const SUPER_ONLY_AREAS = new Set(['companies', 'providers']);

function Forbidden() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 10,
        padding: '64px 24px',
        minHeight: 280,
        border: '1px dashed var(--border)',
        borderRadius: 'var(--r-lg)'
      }}
    >
      <h3 style={{ fontSize: 15.5 }}>Acesso restrito</h3>
      <p style={{ fontSize: 13.5, color: 'var(--text-2)', maxWidth: 380 }}>
        Esta área é exclusiva de super administradores.
      </p>
    </div>
  );
}

function renderArea(area: string, isSuper: boolean) {
  if (SUPER_ONLY_AREAS.has(area) && !isSuper) return <Forbidden />;
  switch (area) {
    case 'companies':
      return <Companies />;
    case 'files':
      return <Files />;
    case 'logs':
      return <Logs />;
    case 'providers':
      return <Providers />;
    case 'templates':
      return <Templates />;
    case 'docs':
      return <Docs />;
    case 'account':
      return <Account />;
    default:
      return <Dashboard />;
  }
}

function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-0)',
        color: 'var(--text-2)',
        fontSize: 13.5
      }}
    >
      Carregando…
    </div>
  );
}

function Shell() {
  const { user, logout, impersonate } = useAuth();
  const toast = useToast();
  const [area, setAreaState] = useState(areaFromUrl);

  useEffect(() => {
    const onPop = () => setAreaState(areaFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function setArea(a: string) {
    setAreaState(a);
    const url = a === 'dashboard' ? window.location.pathname : `${window.location.pathname}?area=${a}`;
    window.history.pushState({ area: a }, '', url);
  }

  async function onLogout() {
    await logout();
    toast('Você saiu.', 'info');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        onLogout={onLogout}
        userInitials={user?.nome?.[0] ?? 'A'}
        userName={user?.nome}
        papel={user?.papel}
        onArea={setArea}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar area={area} onArea={setArea} pendentes={0} papel={user?.papel} />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <ImpersonateBanner />
          <main
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 32px',
              minWidth: 0
            }}
          >
            <AreaErrorBoundary resetKey={`${area}:${impersonate?.company_id ?? 'self'}`}>
              <div key={`${area}:${impersonate?.company_id ?? 'self'}`} className="anim-area">
                {renderArea(area, user?.papel === 'super_admin')}
              </div>
            </AreaErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}

function AuthGate() {
  const { loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Login />;
  return <Shell />;
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AuthGate />
      </ToastProvider>
    </AuthProvider>
  );
}
